import React, { forwardRef } from 'react';
import { cn } from '../../utils/cn';

export interface RangeProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  error?: string;
  helperText?: string;
  showValue?: boolean;
  showMinMax?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
}

export const Range = forwardRef<HTMLInputElement, RangeProps>(
  (
    {
      value,
      onChange,
      min = 0,
      max = 100,
      step = 1,
      label,
      error,
      helperText,
      showValue = false,
      showMinMax = false,
      size = 'md',
      variant = 'default',
      className,
      id,
      disabled,
      ...props
    },
    ref
  ) => {
    const rangeId = id || `range-${Math.random().toString(36).substr(2, 9)}`;

    const sizeClasses = {
      sm: 'h-1',
      md: 'h-2',
      lg: 'h-3',
    };

    const variantClasses = {
      default: 'bg-blue-600',
      primary: 'bg-blue-600',
      success: 'bg-green-600',
      warning: 'bg-yellow-600',
      danger: 'bg-red-600',
    };

    const trackClasses = {
      default: 'bg-gray-200 dark:bg-gray-700',
      primary: 'bg-blue-200 dark:bg-blue-700',
      success: 'bg-green-200 dark:bg-green-700',
      warning: 'bg-yellow-200 dark:bg-yellow-700',
      danger: 'bg-red-200 dark:bg-red-700',
    };

    return (
      <div className={cn('space-y-2', className)}>
        {(label || showValue) && (
          <div className="flex items-center justify-between">
            {label && (
              <label
                htmlFor={rangeId}
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {label}
              </label>
            )}
            {showValue && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {value}
              </span>
            )}
          </div>
        )}

        <div className="relative">
          <input
            ref={ref}
            type="range"
            id={rangeId}
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            disabled={disabled}
            className={cn(
              'w-full appearance-none bg-transparent cursor-pointer',
              'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              sizeClasses[size],
              'rounded-lg',
              // Webkit styles
              '[&::-webkit-slider-track]:appearance-none',
              '[&::-webkit-slider-track]:h-full',
              '[&::-webkit-slider-track]:rounded-lg',
              '[&::-webkit-slider-track]:transition-colors',
              trackClasses[variant],
              // Webkit thumb styles
              '[&::-webkit-slider-thumb]:appearance-none',
              '[&::-webkit-slider-thumb]:rounded-full',
              '[&::-webkit-slider-thumb]:border-2',
              '[&::-webkit-slider-thumb]:border-white',
              '[&::-webkit-slider-thumb]:shadow-lg',
              '[&::-webkit-slider-thumb]:transition-all',
              '[&::-webkit-slider-thumb]:hover:scale-110',
              variantClasses[variant],
              {
                'h-1 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3': size === 'sm',
                'h-2 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4': size === 'md',
                'h-3 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5': size === 'lg',
              },
              error && 'focus:ring-red-500',
              className
            )}
            aria-invalid={!!error}
            aria-describedby={
              error 
                ? `${rangeId}-error` 
                : helperText 
                  ? `${rangeId}-helper` 
                  : undefined
            }
            {...props}
          />
        </div>

        {showMinMax && (
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>{min}</span>
            <span>{max}</span>
          </div>
        )}

        {(error || helperText) && (
          <div>
            {error && (
              <p 
                id={`${rangeId}-error`}
                className="text-sm text-red-600 dark:text-red-400"
                role="alert"
              >
                {error}
              </p>
            )}
            {helperText && !error && (
              <p 
                id={`${rangeId}-helper`}
                className="text-sm text-gray-500 dark:text-gray-400"
              >
                {helperText}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Range.displayName = 'Range'; 